import {
  Card,
  Col,
  Edit,
  Form,
  Input,
  List,
  ListButton,
  Row,
  Select,
  Table,
  Typography,
  useForm,
} from '@pankod/refine-antd';
import { HttpError } from '@pankod/refine-core';
import { ProductFooter } from 'components';
import { ISale } from 'interfaces';
import { formatInvoiceDiscountEntry } from 'utilities/discount-entry';
import { useEffect, useState } from 'react';
import { getClient } from 'services/client';
import { calculateStoredSaleVatAmount, storedVatFractionToPercent } from 'utilities/sale-vat';

interface IClientPointOfContact {
  id: string;
  name: string;
}

const { Option } = Select;
const { Text } = Typography;

interface IProductState {
  id: string;
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
}

export const SaleEdit: React.FC = () => {
  const [products, setProducts] = useState<IProductState[]>([]);
  const [clientPointOfContacts, setClientPointOfContacts] = useState<IClientPointOfContact[]>([]);

  const { form, formProps, saveButtonProps, queryResult } = useForm<ISale, HttpError>({
    metaData: {
      fields: [
        'id',
        'salesAddress',
        'note',
        'salesDate',
        'salesStatus',
        'vatAmount',
        'shipmentCost',
        'discountCost',
        'discountEntryValue',
        'discountEntryType',
        'subTotal',
        {
          clientPoc: ['id', 'name'],
        },
        {
          warehouse: ['id', 'name'],
        },
        'totalAmount',
        {
          client: ['id', 'name'],
        },
        {
          salesProducts: [
            'id',
            'quantity',
            'unitPrice',
            'subTotal',
            {
              product: ['id', 'sku', 'name'],
            },
          ],
        },
      ],
    },
  });
  const client = queryResult?.data?.data?.client;
  const note = queryResult?.data?.data.note;
  const clientPoc = queryResult?.data?.data?.clientPoc;
  const warehouseId = queryResult?.data?.data?.warehouse?.id;
  const warehouseName = queryResult?.data?.data?.warehouse?.name;
  const subTotal = queryResult?.data?.data?.subTotal;
  // vatAmount is stored as a fraction (0.15 for 15%).
  const vatPercentage = storedVatFractionToPercent(queryResult?.data?.data?.vatAmount);
  const vatAmount = calculateStoredSaleVatAmount(subTotal, queryResult?.data?.data?.vatAmount);
  const shipmentCost = queryResult?.data?.data?.shipmentCost;
  const discountCost = queryResult?.data?.data.discountCost;
  const netTotal = queryResult?.data?.data?.totalAmount;
  const allProducts = queryResult?.data?.data?.salesProductsCopy?.map((item) => {
    return {
      id: item.productVariant.id,
      sku: item.productVariant.sku,
      name: item.productVariant.name,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
    };
  });

  useEffect(() => {
    if (client?.id) {
      (async () => {
        try {
          const clientWithPointOfContacts = await getClient(client.id);
          const pointOfContacts = clientWithPointOfContacts?.pointOfContacts ?? [];
          setClientPointOfContacts(pointOfContacts);
        } catch (_) {
          setClientPointOfContacts([]);
        }
      })();
    }
  }, [client]);

  useEffect(() => {
    if (products.length === 0 && allProducts?.length) {
      setProducts(allProducts);
    }
  }, [allProducts, products]);

  const getOptions = (items: IClientPointOfContact[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onFinish = (values: any) => {
    const data = {
      client: values.clientId as string,
      clientPoc: values.clientPoc as string,
      salesAddress: values.salesAddress as string,
      salesDate: queryResult?.data?.data?.salesDate,
      salesStatus: queryResult?.data?.data?.salesStatus,
      warehouse: values.warehouseId as string,
      note: values.note as string,
    };

    return (
      formProps.onFinish &&
      formProps.onFinish({
        ...data,
      })
    );
  };

  const footer = () => {
    return (
      <ProductFooter
        subTotal={subTotal || 0}
        vatAmount={vatAmount}
        vatPercentage={vatPercentage || 0}
        shipmentCost={shipmentCost || 0}
        discountCost={discountCost || 0}
        discountLabel={formatInvoiceDiscountEntry(
          queryResult?.data?.data?.discountEntryValue,
          queryResult?.data?.data?.discountEntryType,
          discountCost,
        )}
        netTotal={netTotal || 0}
      />
    );
  };

  return (
    <Edit
      isLoading={queryResult?.isFetching}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: !products.length,
      }}
      headerProps={{ extra: <ListButton /> }}
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{
          ...formProps.initialValues,
          clientId: client?.id,
          name: client?.name,
          clientPoc: clientPoc?.id,
          warehouseId,
          warehouseName,
          note,
        }}
        form={form}
        onFinish={onFinish}
      >
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={18}>
            <Card title="Sale Information">
              <Form.Item label="Warehouse ID" name="warehouseId" style={{ display: 'none' }}>
                <Input disabled />
              </Form.Item>
              <Form.Item label="Warehouse" name="warehouseName">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Client's ID" name="clientId" style={{ display: 'none' }}>
                <Input disabled />
              </Form.Item>
              <Form.Item label="Client's Name" name="name">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Point Of Contact" name="clientPoc">
                <Select
                  placeholder="Select a POC"
                  filterOption={(input, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(clientPointOfContacts)}
                </Select>
              </Form.Item>
              <Form.Item label="Sale Address" name="salesAddress">
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item label="Note" name="note">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        {/* Products Table */}
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={18}>
            <List
              title="Products"
              pageHeaderProps={{
                extra: null,
              }}
            >
              <Table
                dataSource={products}
                rowKey="id"
                scroll={{ x: true }}
                footer={(_data) => footer()}
              >
                <Table.Column dataIndex="sku" title="SKU" />
                <Table.Column dataIndex="name" title="Product Name" />
                <Table.Column dataIndex="quantity" title="Quantity" />
                <Table.Column dataIndex="unitPrice" title="Unit Price" />
                <Table.Column<IProductState>
                  dataIndex="subTotal"
                  title="Total"
                  render={(_, record) => {
                    return <Text>{record.quantity * record.unitPrice}</Text>;
                  }}
                />
              </Table>
            </List>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
